import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import CiteRef from 'components/report/CiteRef'
import ProcessMath from 'components/misc/ProcessMath'
import ErrorFallback from 'components/errors/ErrorFallback'
import { MODAL_FROM_CONTEXTS } from 'store/ui/actions'
import styles from './styles/Snippet.css'

import BlurOverPaywall from '../misc/BlurOverPaywall'
import Tooltip from '../misc/Tooltip'

const ELEMENT_WHITE_LIST = ['CITE', 'STRONG', 'B']

const PLACEHOLDER_SNIPPET = 'Of note, only right amygdala connectivity was found to be associated with LOC in the present study. This is consistent with previous studies which reported that only the right amygdala was involved in anxiety and borderline personality disorders (Minzenberg, Fan, New, Tang, & Siever, 2007; Phan, Fitzgerald, Nathan, & Tancer, 2006). However, the lateralized effects of amygdala connectivity require further replication, considering the smaller sample size in the current study'

export const cleanDoi = doi => doi.replace(/^doi:/, '').replace(/^https?:\/\/doi.org\//, '')

export const mapJsxFromNode = (reportTarget, linkTarget) => (node, index) => {
  if (node.nodeType === node.TEXT_NODE) {
    return node.textContent
      .replace(/,$/g, ', ')
      .replace(/\.$/g, '. ')
  }

  if (node.tagName === 'CITE') {
    const content = node.innerHTML.includes('<strong class="highlight">')
      ? (
        <strong key={index} className='highlight'>
          {node.textContent}
        </strong>
        )
      : node.textContent
    return (
      <CiteRef
        key={index}
        dois={(node?.dataset?.doi || '').split(' ').map(cleanDoi)}
        reference={content}
        reportTarget={reportTarget}
        linkTarget={linkTarget}
      />
    )
  }

  if (node.tagName === 'STRONG' || node.tagName === 'B') {
    // if there are citations that are highlighted we need to render those too!
    const elArray = []
    let i = 0
    for (const child of node.childNodes) {
      elArray.push(mapJsxFromNode(reportTarget, linkTarget)(child, i))
      i++
    }
    return (
      <strong key={index} className='highlight'>
        {elArray}
      </strong>
    )
  }

  return null
}

const unwrapChild = child => {
  const parent = child.parentNode

  while (child.firstChild) {
    parent.insertBefore(child.firstChild, child)
  }

  parent.removeChild(child)
}

const SnippetText = ({ snippet, blur, children, truncateSnippet, showFullContext, setShowFullContext }) => {
  if (blur) {
    return <BlurOverPaywall contentType='citation statement' placeholder={PLACEHOLDER_SNIPPET} modalFromContext={MODAL_FROM_CONTEXTS.CITATION_STATEMENTS_SEARCH} />
  }

  return (
    <div className={styles.snippetWrapper}>
      <ErrorFallback fallback={children}>
        <ProcessMath>
          <span>{children}</span>
          {truncateSnippet && (
            <Tooltip content={showFullContext ? 'Click to hide full context' : 'Click to view full context'} position='top'>
              <span className={styles.snippetShowHide} onClick={() => setShowFullContext(!showFullContext)}>
                {showFullContext ? '<<' : '>>'}
              </span>
            </Tooltip>
          )}
        </ProcessMath>
      </ErrorFallback>
    </div>
  )
}

export const Snippet = ({ reportTarget, snippet, forceDOM, truncateSnippet, showFullContext, setShowFullContext, linkTarget = false, blur = false }) => {
  const [canUseDOM, setCanUseDOM] = useState(!!forceDOM)
  useEffect(() => {
    setCanUseDOM(true)
  }, [])

  const markup = `&ldquo;&hellip;${snippet}&hellip;&rdquo;`.replace(
    `data-doi="${reportTarget}"`,
    `class="target" data-doi="${reportTarget}"`
  )

  const getElements = useCallback(() => {
    const renderer = document.createElement('div')
    renderer.innerHTML = markup

    //
    // Strip all top level tags except <cite>s
    //
    // Hack to sanitize snippets that container TEI specific tags:
    // https://github.com/scitedotai/scite/issues/973
    //
    for (const child of renderer.childNodes) {
      if (child.tagName && !ELEMENT_WHITE_LIST.includes(child.tagName)) {
        unwrapChild(child)
      }
    }

    return [...renderer.childNodes].map(mapJsxFromNode(reportTarget, linkTarget))
  }, [reportTarget, linkTarget, markup])

  if (canUseDOM) {
    return (
      <SnippetText blur={blur} truncateSnippet={truncateSnippet} showFullContext={showFullContext} setShowFullContext={setShowFullContext}>
        {getElements()}
      </SnippetText>
    )
  }

  return (
    <SnippetText blur={blur} truncateSnippet={truncateSnippet} showFullContext={showFullContext} setShowFullContext={setShowFullContext}>
      <div
        dangerouslySetInnerHTML={{
          __html: markup
        }}
      />
    </SnippetText>
  )
}

Snippet.propTypes = {
  snippet: PropTypes.string.isRequired,
  reportTarget: PropTypes.string.isRequired,
  linkTarget: PropTypes.bool,
  forceDOM: PropTypes.bool,
  blur: PropTypes.bool
}

export default Snippet
